import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ACCENTS = {
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
};

export const KpiTile = ({ label, value, icon: Icon, accent = 'amber', testId }) => {
  const a = ACCENTS[accent] || ACCENTS.amber;
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
        <div className={`w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-lg flex items-center justify-center ${a.bg}`}>
          {Icon && <Icon className={`w-4 h-4 md:w-5 md:h-5 ${a.text}`} />}
        </div>
        <div className="min-w-0">
          <p className="text-lg md:text-2xl font-bold text-[#1A1F2E] leading-tight break-words" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const DateRangeFilter = ({ from, to, onFrom, onTo, onClear, testPrefix, children }) => (
  <div className="flex flex-wrap items-end gap-3 mb-4" data-testid={`${testPrefix}-date-filter`}>
    {children}
    <div>
      <Label className="text-xs text-gray-500">From</Label>
      <Input type="date" value={from} onChange={e => onFrom(e.target.value)} className="h-9 w-[160px]" data-testid={`${testPrefix}-date-from`} />
    </div>
    <div>
      <Label className="text-xs text-gray-500">To</Label>
      <Input type="date" value={to} onChange={e => onTo(e.target.value)} className="h-9 w-[160px]" data-testid={`${testPrefix}-date-to`} />
    </div>
    {(from || to) && (
      <Button variant="ghost" size="sm" onClick={onClear} className="h-9 text-gray-500" data-testid={`${testPrefix}-date-clear`}>Clear</Button>
    )}
  </div>
);

// Inclusive YYYY-MM-DD range check. rowDate is a YYYY-MM-DD string ('' = excluded only when a bound set).
export const inDateRange = (rowDate, from, to) => {
  const d = (rowDate || '').slice(0, 10);
  if (from && (!d || d < from)) return false;
  if (to && (!d || d > to)) return false;
  return true;
};

export const ownershipParams = (v) => (v && v !== 'all' ? { ownership: v } : {});

export const OwnershipFilter = ({ value, onChange, testPrefix }) => (
  <div>
    <Label className="text-xs text-gray-500">Vehicles</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px] bg-white" data-testid={`${testPrefix}-ownership-filter`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={`${testPrefix}-ownership-all`}>All vehicles</SelectItem>
        <SelectItem value="owned" data-testid={`${testPrefix}-ownership-owned`}>Owned only</SelectItem>
        <SelectItem value="hired" data-testid={`${testPrefix}-ownership-hired`}>Hired only</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

export const HiredBadge = ({ ownership, testId }) => (ownership === 'hired'
  ? <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200 rounded px-1 py-0.5 align-middle" data-testid={testId}>Hired</span>
  : null);
